import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import DashboardMoeMorPanel from "./DashboardMoeMorPanel";
import GainMassiqueBoxplot from "./GainMassiqueBoxplot";
import HumiditePlisBoxplot from "./HumiditePlisBoxplot";
import DashboardMasseVolumiqueSection from "./DashboardMasseVolumiqueSection";
import DashboardKpiCard, { DashboardKpiRow } from "./DashboardKpiCard";
import LastStacksPanel from "./LastStacksPanel";

import { useI18n } from "../i18n/I18nContext";

// ─── HELPERS ────────────────────────────────────────────────────────────────

const toF = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "object") {
    v = v?.$n ?? v?.value ?? v?.S ?? Object.values(v || {})[0] ?? 0;
  }
  const n = parseFloat(String(v).replace(",", "."));
  return Number.isNaN(n) ? 0 : n;
};

const q2 = (v) => Math.round(v * 100) / 100;
const pad = (n) => String(n).padStart(2, "0");

const COLORS = {
  blue: "#60a5fa",
  green: "#22c55e",
  yellow: "#E6FF3E",
  red: "#f87171",
  orange: "#fb923c",
  purple: "#a78bfa",
  gray: "#64748b",
};

function parseDateFR(value) {
  if (!value) return null;

  const s = String(value).trim();
  if (!s) return null;

  let m = s.match(
    /^(\d{2})[\/-](\d{2})[\/-](\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (m) {
    const [, dd, mm, yyyy, hh = "00", mi = "00", ss = "00"] = m;
    const d = new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(mi),
      Number(ss)
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }

  m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const [, yyyy, mm, dd] = m;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
  );
  if (m) {
    const [, yyyy, mm, dd, hh, mi, ss = "00"] = m;
    const d = new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(mi),
      Number(ss)
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function getMondayKey(d) {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  const day = date.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(date);
  monday.setDate(date.getDate() + diffToMonday);
  return `${monday.getFullYear()}-${pad(monday.getMonth() + 1)}-${pad(monday.getDate())}`;
}

function formatDayKey(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatShortDate(value) {
  const d = parseDateFR(value);
  if (!d) return String(value ?? "");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
}

function formatLongDate(value) {
  const d = parseDateFR(value);
  if (!d) return String(value ?? "");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function addDays(date, nb) {
  const d = new Date(date);
  d.setDate(d.getDate() + nb);
  return d;
}

// ─── COMPOSANTS GRAPHIQUES ───────────────────────────────────────────────────

function ChartTooltipVolume({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  return (
    <div style={S.tooltip}>
      <div style={{ color: COLORS.yellow, fontWeight: 700, marginBottom: 6, fontSize: 12 }}>
        {label}
      </div>
      <div style={{ color: COLORS.blue, fontSize: 12, marginBottom: 2 }}>
        Nb stacks : <strong>{p?.count ?? 0}</strong>
      </div>
      <div style={{ color: COLORS.green, fontSize: 12 }}>
        Volume total : <strong>{p?.volume ?? 0} m³</strong>
      </div>
    </div>
  );
}

function ChartTooltipEpaisseur({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  return (
    <div style={S.tooltip}>
      <div style={{ color: COLORS.yellow, fontWeight: 700, marginBottom: 6, fontSize: 12 }}>
        {label}
      </div>
      <div style={{ color: COLORS.blue, fontSize: 12, marginBottom: 2 }}>
        Nb stacks : <strong>{p?.count ?? 0}</strong>
      </div>
      <div style={{ color: COLORS.orange, fontSize: 12 }}>
        Épaisseur totale : <strong>{p?.epaisseur ?? 0} mm</strong>
      </div>
    </div>
  );
}

function VolumeChart({ title, data, dataKey, tickFormatter }) {
  return (
    <div style={{ ...S.card, minWidth: 0 }}>
      <div style={S.cardTitle}>{title}</div>
      <div style={{ width: "100%", height: 340 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 20, left: -10, bottom: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
            <XAxis
              dataKey={dataKey}
              tick={{ fill: "#94a3b8", fontSize: 10 }}
              angle={-35}
              textAnchor="end"
              interval={0}
              height={70}
              tickFormatter={tickFormatter}
            />
            <YAxis
              yAxisId="left"
              tick={{ fill: COLORS.blue, fontSize: 11 }}
              allowDecimals={false}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: COLORS.green, fontSize: 11 }}
            />
            <Tooltip content={<ChartTooltipVolume />} />
            <Legend />
            <Bar
              yAxisId="left"
              dataKey="count"
              name="Nb stacks"
              fill={COLORS.blue}
              radius={[4, 4, 0, 0]}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="volume"
              name="Volume total (m³)"
              stroke={COLORS.green}
              strokeWidth={3}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function EpaisseurChart({ title, data, dataKey, tickFormatter }) {
  return (
    <div style={S.card}>
      <div style={S.cardTitle}>{title}</div>
      <div style={{ width: "100%", height: 340, marginTop: 12 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 20, left: -10, bottom: 50 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
            <XAxis
              dataKey={dataKey}
              tick={{ fill: "#94a3b8", fontSize: 10 }}
              angle={-35}
              textAnchor="end"
              interval={0}
              height={70}
              tickFormatter={tickFormatter}
            />
            <YAxis
              yAxisId="left"
              tick={{ fill: COLORS.blue, fontSize: 11 }}
              allowDecimals={false}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: COLORS.orange, fontSize: 11 }}
            />
            <Tooltip content={<ChartTooltipEpaisseur />} />
            <Legend />
            <Bar
              yAxisId="left"
              dataKey="count"
              name="Nb stacks"
              fill={COLORS.blue}
              radius={[4, 4, 0, 0]}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="epaisseur"
              name="Épaisseur totale (mm)"
              stroke={COLORS.orange}
              strokeWidth={3}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ─── SOUS-MENUS ─────────────────────────────────────────────────────────────

const SUB_TABS = [
  { id: "production", labelKey: "dash.tabProduction" },
  { id: "moe-mor", labelKey: "dash.tabMoeMor" },
  { id: "gain-massique", labelKey: "dash.tabGain" },
  { id: "humidite-pli", labelKey: "dash.tabHumidite" },
  { id: "masse-volumique", labelKey: "dash.tabMasseVol" },
];

const SUB_TAB_SUBTITLES = {
  production: "Volume · Épaisseur · Conformité · Derniers stacks",
  "moe-mor": "MOE E0,05 · MOR fm,k — valeurs caractéristiques EN 14358",
  "gain-massique": "Boxplot gain massique moyen par stack",
  "humidite-pli": "Humidité banc de test / conditionnement / imprégnation / séchage",
  "masse-volumique": "Valeur caractéristique mensuelle de la masse volumique (EN 14358)",
};

// ─── COMPOSANT PRINCIPAL ─────────────────────────────────────────────────────

export default function DashboardProduction() {
  const { t, ui } = useI18n();

  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("production");
  const [productionLoaded, setProductionLoaded] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [empList, setEmpList] = useState([]);
  const [impregList, setImpregList] = useState([]);
  const [epaisseurMap, setEpaisseurMap] = useState({});
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState("");
  const [conformity, setConformity] = useState("ALL");
  const [epaisseurPage, setEpaisseurPage] = useState(1);
  const [selectedWeek, setSelectedWeek] = useState("ALL");
  const [periodMode, setPeriodMode] = useState("DAYS");
const [selectedYear, setSelectedYear] = useState("ALL");


  const EP_ROWS_PER_PAGE = 10;

  useEffect(() => {
    setSelectedWeek("ALL");
  },  [days, selectedYear, periodMode]);

  useEffect(() => {
    if (activeTab !== "production" || productionLoaded) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const results = await Promise.allSettled([
          apiFetch("/api/qualite/stack/list"),
          apiFetch("/api/empilement/list/impregnation"),
          apiFetch("/api/impregnation?per_page=500"),
          apiFetch("/api/empilement/epaisseur-par-stack"),
        ]);

        const safeJson = async (r) => {
          if (r.status === "rejected") return null;
          try {
            return await r.value.json();
          } catch {
            return null;
          }
        };

        const [qualiteRaw, empRaw, impregRaw, epaisseurRaw] = await Promise.all(
          results.map(safeJson)
        );

        if (!cancelled) {
          setItems(
            Array.isArray(qualiteRaw)
              ? qualiteRaw
              : qualiteRaw?.items && Array.isArray(qualiteRaw.items)
              ? qualiteRaw.items
              : []
          );

          setEmpList(
            Array.isArray(empRaw)
              ? empRaw
              : empRaw?.items && Array.isArray(empRaw.items)
              ? empRaw.items
              : []
          );

          setImpregList(
            Array.isArray(impregRaw)
              ? impregRaw
              : impregRaw?.items && Array.isArray(impregRaw.items)
              ? impregRaw.items
              : []
          );

          setEpaisseurMap(
            epaisseurRaw?.items && typeof epaisseurRaw.items === "object"
              ? epaisseurRaw.items
              : {}
          );
          setProductionLoaded(true);
        }
      } catch (e) {
        if (!cancelled) setError("Erreur chargement données.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, productionLoaded]);

  const uncontrolledStackIds = useMemo(() => {
    const controlledIds = new Set(
      items
        .map((s) => String(s.IDStack || s.StackId || "").trim())
        .filter(Boolean)
    );

    return empList
      .map((e) => String(e.StackId || e.IDStack || "").trim())
      .filter(Boolean)
      .filter((id) => !controlledIds.has(id));
  }, [items, empList]);

  const counters = useMemo(() => {
    const conforme = items.filter(
      (s) => String(s.Conformite || "").trim().toUpperCase() === "CONFORME"
    ).length;

    const nonConforme = items.filter(
      (s) => String(s.Conformite || "").trim().toUpperCase() === "NON CONFORME"
    ).length;

    const epaisseurVals = Object.values(epaisseurMap)
      .map((e) => e.epaisseurTotale)
      .filter((v) => v > 0);

    const avgEpaisseur =
      epaisseurVals.length > 0
        ? q2(epaisseurVals.reduce((a, b) => a + b, 0) / epaisseurVals.length)
        : 0;

    return {
      conforme,
      nonConforme,
      nonControle: uncontrolledStackIds.length,
      totalEmpilement: empList.length,
      avgEpaisseur,
    };
  }, [items, empList, uncontrolledStackIds, epaisseurMap]);


  const filtered = useMemo(() => {
  const uncontrolledSet = new Set(uncontrolledStackIds);

  const isInSelectedPeriod = (rawDate) => {
    const d = parseDateFR(rawDate);
    if (!d) return false;

    d.setHours(0, 0, 0, 0);

    if (periodMode === "YEAR") {
      if (selectedYear === "ALL") return true;
      return d.getFullYear() === Number(selectedYear);
    }

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    cutoff.setHours(0, 0, 0, 0);
    return d >= cutoff;
  };

  const qualityRows = items.filter((r) => {
    const d = parseDateFR(r.Date) || parseDateFR(r.Timestamp);
    if (!d) return false;

    const inPeriod = isInSelectedPeriod(r.Date || r.Timestamp);
    const id = String(r.IDStack || r.StackId || "").trim();

    const inSearch =
      !search.trim() || id.toLowerCase().includes(search.trim().toLowerCase());

    const confValue = String(r.Conformite || "").trim().toUpperCase();

    const inConformity =
      conformity === "ALL"
        ? true
        : conformity === "CONFORME"
        ? confValue === "CONFORME"
        : conformity === "NON_CONFORME"
        ? confValue === "NON CONFORME"
        : conformity === "NON_CONTROLE"
        ? false
        : true;

    return inPeriod && inSearch && inConformity;
  });

  if (conformity !== "NON_CONTROLE") return qualityRows;

  return empList
    .filter((e) => {
      const id = String(e.StackId || e.IDStack || "").trim();
      if (!uncontrolledSet.has(id)) return false;

      const inPeriod = isInSelectedPeriod(e.Date || e.Timestamp);

      const inSearch =
        !search.trim() || id.toLowerCase().includes(search.trim().toLowerCase());

      return inPeriod && inSearch;
    })
    .map((e) => ({
      IDStack: e.StackId || e.IDStack,
      Date: e.Date || null,
      Timestamp: e.Timestamp || null,
      Conformite: "NON CONTROLE",
      TotalPlis: toF(e.TotalPlis),
      Longueur: toF(e.Longueur),
      Largeur: toF(e.Largeur),
      Epaisseur: toF(e.Epaisseur),
      LongueurMoyenne: toF(e.LongueurMoyenne ?? e.Longueur),
      LargeurMoyenne: toF(e.LargeurMoyenne ?? e.Largeur),
      EpaisseurMoyenne: toF(e.EpaisseurMoyenne ?? e.Epaisseur),
    }));
}, [items, empList, uncontrolledStackIds, search, conformity, days, selectedYear, periodMode]);

  const weeklyVolumeData = useMemo(() => {
    const grouped = {};

    filtered.forEach((r) => {
      const d = parseDateFR(r.Date) || parseDateFR(r.Timestamp);
      if (!d) return;

      const longueur = toF(
        r.LongueurMoyenne ?? r.Longueur_Stack ?? r.Longueur ?? 0
      );
      const largeur = toF(
        r.LargeurMoyenne ?? r.Largeur_Stack ?? r.Largeur ?? 0
      );
      const epaisseur = toF(
        r.EpaisseurMoyenne ?? r.Epaisseur_Moyenne ?? r.Epaisseur ?? 0
      );

      const volume = longueur * largeur * epaisseur * 0.000000001;
      if (volume <= 0) return;

      const key = getMondayKey(d);
      if (!grouped[key]) grouped[key] = { week: key, count: 0, volume: 0 };

      grouped[key].count += 1;
      grouped[key].volume += volume;
    });

    return Object.values(grouped)
      .map((x) => ({ ...x, volume: q2(x.volume) }))
      .sort((a, b) => a.week.localeCompare(b.week));
  }, [filtered]);

  const monthlyVolumeData = useMemo(() => {
    const grouped = {};

    filtered.forEach((r) => {
      const d = parseDateFR(r.Date) || parseDateFR(r.Timestamp);
      if (!d) return;

      const longueur = toF(
        r.LongueurMoyenne ?? r.Longueur_Stack ?? r.Longueur ?? 0
      );
      const largeur = toF(
        r.LargeurMoyenne ?? r.Largeur_Stack ?? r.Largeur ?? 0
      );
      const epaisseur = toF(
        r.EpaisseurMoyenne ?? r.Epaisseur_Moyenne ?? r.Epaisseur ?? 0
      );

      const volume = longueur * largeur * epaisseur * 0.000000001;
      if (volume <= 0) return;

      const key = `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
      if (!grouped[key]) grouped[key] = { month: key, count: 0, volume: 0 };

      grouped[key].count += 1;
      grouped[key].volume += volume;
    });

    return Object.values(grouped)
      .map((x) => ({ ...x, volume: q2(x.volume) }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }, [filtered]);

 const weeklyEpaisseurData = useMemo(() => {
  const grouped = {};

  const isInSelectedPeriod = (rawDate) => {
    const d = parseDateFR(rawDate);
    if (!d) return false;

    d.setHours(0, 0, 0, 0);

    if (periodMode === "YEAR") {
      if (selectedYear === "ALL") return true;
      return d.getFullYear() === Number(selectedYear);
    }

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    cutoff.setHours(0, 0, 0, 0);
    return d >= cutoff;
  };

  empList.forEach((emp) => {
    const stackId = String(emp.StackId || emp.IDStack || "").trim();
    if (!stackId) return;

    const entry = epaisseurMap[stackId];
    if (!entry || entry.epaisseurTotale <= 0) return;

    const rawDate = emp.Date || emp.Timestamp;
    const d = parseDateFR(rawDate);
    if (!d || !isInSelectedPeriod(rawDate)) return;

    const key = getMondayKey(d);
    if (!grouped[key]) grouped[key] = { week: key, count: 0, epaisseur: 0 };

    grouped[key].count += 1;
    grouped[key].epaisseur = q2(grouped[key].epaisseur + entry.epaisseurTotale);
  });

  return Object.values(grouped).sort((a, b) => a.week.localeCompare(b.week));
}, [empList, epaisseurMap, days, selectedYear, periodMode]);

  const weekOptions = useMemo(() => {
    const weekMap = new Map();

    filtered.forEach((r) => {
      const d = parseDateFR(r.Date) || parseDateFR(r.Timestamp);
      if (!d) return;

      const weekKey = getMondayKey(d);
      if (!weekMap.has(weekKey)) {
        weekMap.set(weekKey, { week: weekKey, count: 0 });
      }

      weekMap.get(weekKey).count += 1;
    });

    return Array.from(weekMap.values())
      .sort((a, b) => a.week.localeCompare(b.week))
      .map((w, i) => ({
        ...w,
        code: `S${i + 1}`,
        label: `S${i + 1} · ${formatShortDate(w.week)}`,
      }));
  }, [filtered]);

  const selectedWeekObj = useMemo(() => {
    if (selectedWeek === "ALL") return null;
    return weekOptions.find((w) => w.code === selectedWeek) || null;
  }, [selectedWeek, weekOptions]);

  const selectedWeekKey = selectedWeekObj?.week || null;

  const dailyVolumeData = useMemo(() => {
    if (!selectedWeekKey) return [];

    const start = parseDateFR(selectedWeekKey);
    if (!start) return [];

    const grouped = {};

    for (let i = 0; i < 7; i++) {
      const d = addDays(start, i);
      const key = formatDayKey(d);
      grouped[key] = { day: key, count: 0, volume: 0 };
    }

    filtered.forEach((r) => {
      const d = parseDateFR(r.Date) || parseDateFR(r.Timestamp);
      if (!d) return;

      const dayKey = formatDayKey(d);
      if (!grouped[dayKey]) return;

      const longueur = toF(
        r.LongueurMoyenne ?? r.Longueur_Stack ?? r.Longueur ?? 0
      );
      const largeur = toF(
        r.LargeurMoyenne ?? r.Largeur_Stack ?? r.Largeur ?? 0
      );
      const epaisseur = toF(
        r.EpaisseurMoyenne ?? r.Epaisseur_Moyenne ?? r.Epaisseur ?? 0
      );

      const volume = longueur * largeur * epaisseur * 0.000000001;
      if (volume <= 0) return;

      grouped[dayKey].count += 1;
      grouped[dayKey].volume += volume;
    });

    return Object.values(grouped)
      .map((x) => ({ ...x, volume: q2(x.volume) }))
      .sort((a, b) => a.day.localeCompare(b.day));
  }, [filtered, selectedWeekKey]);

const dailyEpaisseurData = useMemo(() => {
  if (!selectedWeekKey) return [];

  const start = parseDateFR(selectedWeekKey);
  if (!start) return [];

  const grouped = {};

  const isInSelectedPeriod = (rawDate) => {
    const d = parseDateFR(rawDate);
    if (!d) return false;

    d.setHours(0, 0, 0, 0);

    if (periodMode === "YEAR") {
      if (selectedYear === "ALL") return true;
      return d.getFullYear() === Number(selectedYear);
    }

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    cutoff.setHours(0, 0, 0, 0);
    return d >= cutoff;
  };

  for (let i = 0; i < 7; i++) {
    const d = addDays(start, i);
    const key = formatDayKey(d);
    grouped[key] = { day: key, count: 0, epaisseur: 0 };
  }

  empList.forEach((emp) => {
    const stackId = String(emp.StackId || emp.IDStack || "").trim();
    if (!stackId) return;

    const entry = epaisseurMap[stackId];
    if (!entry || entry.epaisseurTotale <= 0) return;

    const rawDate = emp.Date || emp.Timestamp;
    const d = parseDateFR(rawDate);
    if (!d || !isInSelectedPeriod(rawDate)) return;

    const dayKey = formatDayKey(d);
    if (!grouped[dayKey]) return;

    grouped[dayKey].count += 1;
    grouped[dayKey].epaisseur = q2(grouped[dayKey].epaisseur + entry.epaisseurTotale);
  });

  return Object.values(grouped).sort((a, b) => a.day.localeCompare(b.day));
}, [empList, epaisseurMap, selectedWeekKey, days, selectedYear, periodMode]);

  const displayedVolumeData = selectedWeekKey ? dailyVolumeData : weeklyVolumeData;
  const displayedEpaisseurData = selectedWeekKey ? dailyEpaisseurData : weeklyEpaisseurData;

  const volumeDataKey = selectedWeekKey ? "day" : "week";
  const epaisseurDataKey = selectedWeekKey ? "day" : "week";

  const volumeTitle = selectedWeekKey
    ? `Volume par jour — ${selectedWeekObj?.code}`
    : "Volume par semaine (m³)";

  const epaisseurTitle = selectedWeekKey
    ? `Épaisseur par jour — ${selectedWeekObj?.code}`
    : "Épaisseur initiale des empilements par semaine (mm)";

  const epaisseurRows = useMemo(() => {
    return Object.values(epaisseurMap).sort(
      (a, b) => b.epaisseurTotale - a.epaisseurTotale
    );
  }, [epaisseurMap]);

  const epaisseurTotalPages = Math.max(
    1,
    Math.ceil(epaisseurRows.length / EP_ROWS_PER_PAGE)
  );

  const paginatedEpaisseurRows = useMemo(() => {
    const start = (epaisseurPage - 1) * EP_ROWS_PER_PAGE;
    return epaisseurRows.slice(start, start + EP_ROWS_PER_PAGE);
  }, [epaisseurRows, epaisseurPage]);

  return (
    <div style={S.page}>
      <div style={S.wrap}>
        <div style={S.header}>
          <div>
            <div style={S.title}>{t("dash.production")}</div>
            <div style={S.subtitle}>{SUB_TAB_SUBTITLES[activeTab]}</div>
          </div>
          <button style={S.backBtn} onClick={() => navigate(-1)}>
            {ui("Retour")}
          </button>
        </div>

        <div style={S.subTabRow}>
          {SUB_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              style={{
                ...S.subTab,
                ...(activeTab === tab.id ? S.subTabActive : {}),
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>

        {activeTab === "production" && (
          <>
        {loading && (
          <div style={{ color: COLORS.yellow, padding: 24, textAlign: "center" }}>{ui("Chargement...")}</div>
        )}
        {error && (
          <div style={{ color: COLORS.red, padding: 24, textAlign: "center" }}>{error}</div>
        )}
        {!loading && !error && (
          <>
        <DashboardKpiRow>
          <DashboardKpiCard
            label="Stacks empilement"
            value={counters.totalEmpilement}
            sub="source empilement"
            color={COLORS.blue}
          />
          <DashboardKpiCard
            label="Conformes"
            value={counters.conforme}
            sub="contrôlés qualité"
            color={COLORS.green}
          />
          <DashboardKpiCard
            label="Non conformes"
            value={counters.nonConforme}
            sub="contrôlés qualité"
            color={COLORS.red}
          />
          <DashboardKpiCard
            label="Non contrôlés"
            value={counters.nonControle}
            sub="empilement − qualité"
            color={COLORS.yellow}
          />
          <DashboardKpiCard
            label="Épaisseur moy. stack"
            value={`${counters.avgEpaisseur} mm`}
            sub="somme des plis / stack"
            color={COLORS.orange}
          />
        </DashboardKpiRow>

        <div style={S.card}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div style={S.cardTitle}>Filtres</div>

            <input
              style={{ ...S.searchInput, width: 260 }}
              placeholder="Rechercher un IDStack"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <select
              style={S.select}
              value={conformity}
              onChange={(e) => setConformity(e.target.value)}
            >
              <option value="ALL">Tous</option>
              <option value="CONFORME">Conforme</option>
              <option value="NON_CONFORME">Non conforme</option>
              <option value="NON_CONTROLE">Non contrôlé</option>
            </select>

           <select
  style={S.select}
  value={periodMode}
  onChange={(e) => setPeriodMode(e.target.value)}
>
  <option value="DAYS">Période glissante</option>
  <option value="YEAR">Année</option>
</select>

  <select
  style={S.select}
  value={selectedWeek}
  onChange={(e) => setSelectedWeek(e.target.value)}
>
  <option value="ALL">Toutes les semaines</option>
  {weekOptions.map((w) => (
    <option key={w.week} value={w.code}>
      {w.label}
    </option>
  ))}
</select>

{periodMode === "DAYS" ? (
  <select
    style={S.select}
    value={days}
    onChange={(e) => setDays(Number(e.target.value))}
  >
    <option value={7}>7 jours</option>
    <option value={30}>30 jours</option>
    <option value={90}>90 jours</option>
  </select>
) : (
  <select
    style={S.select}
    value={selectedYear}
    onChange={(e) => setSelectedYear(e.target.value)}
  >
    <option value="ALL">Toutes les années</option>
    <option value="2026">2026</option>
    <option value="2025">2025</option>
  </select>
)}

            <div style={S.badge}>
              {filtered.length} stack(s) affiché(s)
            </div>

            {selectedWeekObj && (
              <div style={S.badge}>
                Vue détail : {selectedWeekObj.code} — semaine du {formatLongDate(selectedWeekObj.week)}
              </div>
            )}
          </div>
        </div>

        <div style={S.row}>
          <VolumeChart
            title={volumeTitle}
            data={displayedVolumeData}
            dataKey={volumeDataKey}
            tickFormatter={(v) => formatShortDate(v)}
          />

          <VolumeChart
            title="Volume par mois (m³)"
            data={monthlyVolumeData}
            dataKey="month"
            tickFormatter={(v) => {
              const [y, m] = String(v).split("-");
              return `${m}/${y}`;
            }}
          />
        </div>

        <EpaisseurChart
          title={epaisseurTitle}
          data={displayedEpaisseurData}
          dataKey={epaisseurDataKey}
          tickFormatter={(v) => formatShortDate(v)}
        />

        <div style={S.card}>
          <div style={S.cardTitle}>Épaisseur initiale par stack</div>
          <div style={{ overflowX: "auto", marginTop: 14 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.04)" }}>
                  {["Stack ID", "Nb plis", "Plis avec épaisseur", "Épaisseur totale (mm)"].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 14px",
                        color: "#94a3b8",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        fontSize: 10,
                        textAlign: "left",
                        borderBottom: "1px solid #1e3a50",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedEpaisseurRows.map((row, i) => (
                  <tr
                    key={row.stackId}
                    style={{
                      background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)",
                      borderBottom: "1px solid #1e3a50",
                    }}
                  >
                    <td style={{ padding: "8px 14px", color: COLORS.yellow, fontWeight: 700 }}>
                      {row.stackId}
                    </td>
                    <td style={{ padding: "8px 14px", color: "#94a3b8" }}>
                      {row.nbPlis}
                    </td>
                    <td style={{ padding: "8px 14px", color: "#94a3b8" }}>
                      {row.nbPlisAvecEpaisseur}
                    </td>
                    <td style={{ padding: "8px 14px", color: COLORS.orange, fontWeight: 700 }}>
                      {row.epaisseurTotale} mm
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                marginTop: 14,
                flexWrap: "wrap",
              }}
            >
              <div style={{ color: "#94a3b8", fontSize: 12 }}>
                Page {epaisseurPage} / {epaisseurTotalPages} — {epaisseurRows.length} stack(s)
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  style={{
                    ...S.pageBtn,
                    opacity: epaisseurPage === 1 ? 0.5 : 1,
                    cursor: epaisseurPage === 1 ? "not-allowed" : "pointer",
                  }}
                  disabled={epaisseurPage === 1}
                  onClick={() => setEpaisseurPage((p) => Math.max(1, p - 1))}
                >
                  Précédent
                </button>

                <button
                  style={{
                    ...S.pageBtn,
                    opacity: epaisseurPage === epaisseurTotalPages ? 0.5 : 1,
                    cursor: epaisseurPage === epaisseurTotalPages ? "not-allowed" : "pointer",
                  }}
                  disabled={epaisseurPage === epaisseurTotalPages}
                  onClick={() =>
                    setEpaisseurPage((p) => Math.min(epaisseurTotalPages, p + 1))
                  }
                >
                  Suivant
                </button>
              </div>
            </div>
          </div>
        </div>

        <div style={S.card}>
          <LastStacksPanel
            items={items}
            empList={empList}
            epaisseurMap={epaisseurMap}
            impregList={impregList}
          />
        </div>
          </>
        )}
          </>
        )}

        {activeTab === "moe-mor" && (
          <DashboardMoeMorPanel active />
        )}

        {activeTab === "gain-massique" && (
          <GainMassiqueBoxplot active />
        )}

        {activeTab === "humidite-pli" && (
          <HumiditePlisBoxplot active />
        )}

        {activeTab === "masse-volumique" && (
          <DashboardMasseVolumiqueSection active />
        )}

      </div>
    </div>
  );
}

// ─── STYLES ──────────────────────────────────────────────────────────────────

const S = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(160deg,#041e2e 0%,#063549 60%,#052a3e 100%)",
    color: "#e2e8f0",
    fontFamily: "Inter, sans-serif",
    padding: "24px 28px",
  },
  wrap: {
    maxWidth: 1600,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontSize: 26,
    fontWeight: 900,
    background: "linear-gradient(90deg,#fff,#E6FF3E)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  subtitle: {
    fontSize: 13,
    color: "#94a3b8",
    marginTop: 4,
  },
  backBtn: {
    padding: "9px 18px",
    borderRadius: 8,
    border: "1px solid #1e3a50",
    background: "rgba(255,255,255,0.04)",
    color: "#cbd5e1",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 13,
  },
  subTabRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 4,
  },
  subTab: {
    padding: "10px 18px",
    borderRadius: 8,
    border: "1px solid #1e3a50",
    background: "rgba(255,255,255,0.04)",
    color: "#94a3b8",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  subTabActive: {
    borderColor: COLORS.yellow,
    color: COLORS.yellow,
    background: "rgba(230,255,62,0.08)",
  },
  card: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid #1e3a50",
    borderRadius: 14,
    padding: "20px 22px",
    backdropFilter: "blur(8px)",
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 4,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
  },
  searchInput: {
    padding: "8px 14px",
    borderRadius: 8,
    border: "1px solid #1e3a50",
    background: "rgba(255,255,255,0.04)",
    color: "#e2e8f0",
    fontSize: 13,
    outline: "none",
  },
  select: {
    padding: "8px 14px",
    borderRadius: 8,
    border: "1px solid #1e3a50",
    background: "#082131",
    color: "#e2e8f0",
    fontSize: 13,
    outline: "none",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 10px",
    borderRadius: 20,
    fontWeight: 700,
    fontSize: 11,
    border: "1px solid rgba(230,255,62,0.25)",
    background: "rgba(230,255,62,0.08)",
    color: COLORS.yellow,
  },
  pageBtn: {
    padding: "8px 14px",
    borderRadius: 8,
    border: "1px solid #1e3a50",
    background: "rgba(255,255,255,0.04)",
    color: "#e2e8f0",
    fontSize: 12,
    fontWeight: 700,
  },
  tooltip: {
    background: "#071e2f",
    border: "1px solid #1e3a50",
    borderRadius: 8,
    padding: "10px 14px",
  },
};